import {
  ClockCircleOutlined,
  EnvironmentOutlined,
  MoreOutlined
} from "@ant-design/icons"
import { Button, Col, Dropdown, Flex, Row, Space } from "antd"
import { EXPERIENCE_STATES } from "constants/experiences"
import PropTypes from "prop-types"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { Tag } from "ui/Tag"
import { Paragraph, Text, Title } from "ui/Typography"
import { formatDuration, getActionConditions, getDropdownItems } from "../../utils/experience"
import styles from "./ExperienceCard.module.css"
import { useNavigate } from "react-router-dom"
import { routes } from "router"
import { getMenuConfig } from "./MenuConfig"
import ImagePlaceholder from "components/common/ImagePlaceholder"
import UserSvg from "assets/icons/ex-user.svg"

export const ExperienceCard = ({
  trip,
  onShare,
  uiType,
  onDuplicate,
  onDeactivate,
  onDelete,
  onPublish
}) => {
  const { t } = useTranslation()
  const {
    title,
    status,
    flat_rate,
    price_per_person,
    address,
    max_guests,
    trip_length,
    available_hours,
    photos,
    publish_on_guides_site,
    publish_on_platform
  } = trip

  const price = flat_rate || price_per_person || 0
  const published = status === EXPERIENCE_STATES.published

  const menuConfig = useMemo(() => getMenuConfig(t, {
    onShare,
    onDuplicate,
    onDeactivate,
    onDelete,
    onPublish
  }), [t, onShare, onDuplicate, onDeactivate, onDelete, onPublish])



  const conditions = useMemo(() => getActionConditions(status), [status])
  const dropdownItems = useMemo(() => getDropdownItems(menuConfig, conditions), [menuConfig, conditions])
  const navigate = useNavigate()

  const handleEdit = () => {
    if (uiType === "deactivated") {
      navigate(routes?.proEditExperienceDeactivateBackRoutePathDetails({ id: trip?.id }));
    }
    else if (uiType === "draft") {
      navigate(routes?.proNewListingDetailsPath({ id: trip?.id }));
    }
    else if (uiType === "published") {
      navigate(routes?.proEditExperiencePublishBackRoutePathDetails({ id: trip?.id }));
    }
  }


  const handlePreviewClick = () => {
    if (uiType === "deactivated") {
      navigate(routes.proEditExperienceDeactiveBackRoutePath({ id: trip?.id }));
    } else {
      navigate(routes.proEditExperiencePublishBackRoutePath({ id: trip?.id }));
    }
  };

  const isDeactivated = status === EXPERIENCE_STATES.deactivated;
  const filteredDropdownItems = isDeactivated
    ? dropdownItems.filter(item => item.key === 'publish' || item.key === 'delete')
    : dropdownItems;

  const mainPhoto = useMemo(() => {
    return Array.isArray(photos) ? photos.find(p => p.position === 0) : null;
  }, [photos]);

  return (
    <div className={styles.cardWrapper}>
      <div className={styles.imgWrapper}>
        {mainPhoto ? (
          <img src={mainPhoto.preview_url} alt={title} className={styles.img} />
        ) : (
          <ImagePlaceholder className={styles.img} label={false} />
        )}
      </div>

      <div className={styles.content}>
        <div>
          <Row justify="space-between" align="top">
            <Col>
              <Title level={5} style={{ margin: 0, fontWeight: 500 }} className="pb-xs">
                {title}
              </Title>
            </Col>
            <Col>
              <Flex gap={8}>
                <Text className="color-text-secondary">from</Text>
                <Text className="text-color-primary" strong>
                  ${parseFloat(price).toFixed(2)}
                </Text>
              </Flex>
            </Col>
          </Row>
          <Row justify="space-between" align="top">
            {address && <Col xxl={14} xl={14} span={24} className="locationText">
              <Space align="top">
                <EnvironmentOutlined className="color-text-secondary-dark" />
                <Paragraph ellipsis={{ rows: 2 }} className="mb-0 color-text-secondary-dark">
                  {address}
                </Paragraph>
              </Space>
            </Col>}
            <Col>
              <Space size={16}>
                <Space>
                  <img src={UserSvg} alt="" />
                  <Text className="lowercased color-text-secondary-dark">
                    {max_guests} {t("global.max")}
                  </Text>
                </Space>
                <Space>
                  <ClockCircleOutlined />
                  <Text className="color-text-secondary-dark">
                    {formatDuration(trip_length, available_hours)}
                  </Text>
                </Space>
              </Space>
            </Col>
          </Row>
          {published && (
            <Row className="mt-xs">
              <Col span={24}>
                <Flex gap={5} align="center" wrap="wrap" className={styles.tagCardWraper}>
                  <Text variant="caption" className={`mr-xs ${styles.status}`}>
                    {t("experiences.empty_state.published")}
                  </Text>
                  {publish_on_platform && (
                    <Tag type="success">
                      <Flex gap={4} align="center">
                        <Icon type="feather" name="Check" color="currentColor" width={16} height={16} />
                        {t("global.ds_marketplace")}
                      </Flex>
                    </Tag>
                  )}
                  {publish_on_guides_site && (
                    <Tag type="success">
                      <Flex gap={4} align="center">
                        <Icon type="feather" name="Check" color="currentColor" width={16} height={16} />
                        {t("global.guides_website")}
                      </Flex>
                    </Tag>
                  )}
                </Flex>
              </Col>
            </Row>
          )}
        </div>
        <Row justify="space-between" align="middle" className={styles.publishCard}>
          <Col>
            <Space>
              <Button
                color="primary"
                variant="outlined"
                onClick={handleEdit}
              >
                {status === EXPERIENCE_STATES.draft
                  ? t("global.continue_creating")
                  : t("global.edit")}
              </Button>
              {status !== EXPERIENCE_STATES.draft && (
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={handlePreviewClick}
                >
                  {t("global.preview")}
                </Button>
              )}
            </Space>
          </Col>
          <Col>
            <Dropdown
              menu={{
                items: filteredDropdownItems,
                onClick: (info) => {
                  const item = filteredDropdownItems.find(i => i.key === info.key);
                  if (item && typeof item.onClick === 'function') {
                    item.onClick();
                  }
                }
              }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <div className={styles.actionButtonWrapper}>
                <Button
                  color="primary"
                  variant="text"
                  icon={<MoreOutlined />}
                />
              </div>
            </Dropdown>
          </Col>
        </Row>
      </div>
    </div>
  )
}
ExperienceCard.propTypes = {
  trip: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    status: PropTypes.oneOf(Object.values(EXPERIENCE_STATES)).isRequired,
    flat_rate: PropTypes.number,
    price_per_person: PropTypes.number,
    address: PropTypes.string,
    max_guests: PropTypes.number.isRequired,
    trip_length: PropTypes.oneOf(['full_day', 'half_day']),
    available_hours: PropTypes.arrayOf(
      PropTypes.shape({
        start: PropTypes.string,
        end: PropTypes.string
      })
    ),
    photos: PropTypes.arrayOf(
      PropTypes.shape({
        preview_url: PropTypes.string.isRequired
      })
    ),
    publish_on_guides_site: PropTypes.bool,
    publish_on_platform: PropTypes.bool,
  }).isRequired,
  uiType: PropTypes.oneOf(["draft", "published", "deactivated", "unpublished"]),
  onShare: PropTypes.func,
  onDuplicate: PropTypes.func,
  onDeactivate: PropTypes.func,
  onDelete: PropTypes.func,
  onPublish: PropTypes.func,
}

ExperienceCard.defaultProps = {
  uiType: "published",
  onShare: () => { },
  onDuplicate: () => { },
  onDeactivate: () => { },
  onDelete: () => { },
  onPublish: () => { },
}

